import React, { useMemo } from 'react';
import { XAxis, YAxis, CartesianGrid, Tooltip, Scatter, ResponsiveContainer, ReferenceArea, Area, ComposedChart, Line } from 'recharts';
import {
  calculateAverage,
  calculateTrend,
  getEarliestLatestFingers,
  getWorstOffendersSync,
  prepareTrendOverRepetitionData,
  prepareSyncTrendOverRepetitionData,
  formatLateness,
  calculateLinearRegression,
  calculateSynchronization
} from '../../utils/latenessUtils';

// Helper to calculate standard deviation
const calculateStdDev = (values) => {
  if (values.length === 0) return 0;
  const mean = values.reduce((acc, val) => acc + val, 0) / values.length;
  const squaredDiffs = values.map(val => Math.pow(val - mean, 2));
  const variance = squaredDiffs.reduce((acc, val) => acc + val, 0) / values.length;
  return Math.sqrt(variance);
};

// Helper to calculate symmetric Y-axis range
const getSymmetricYDomainForRegression = (regressionData) => {
  if (!regressionData || regressionData.length === 0) return [-50, 50];
  
  let maxAbs = 0;
  regressionData.forEach(series => {
    series.data.forEach(point => {
      const upper = Math.abs(point.value + (series.stdDev || 0));
      const lower = Math.abs(point.value - (series.stdDev || 0));
      maxAbs = Math.max(maxAbs, upper, lower);
    });
  });
  
  const rangeWithPadding = maxAbs * 1.2;
  
  let range;
  if (rangeWithPadding <= 10) {
    range = Math.ceil(rangeWithPadding);
  } else if (rangeWithPadding <= 25) {
    range = Math.ceil(rangeWithPadding / 5) * 5;
  } else if (rangeWithPadding <= 50) {
    range = Math.ceil(rangeWithPadding / 10) * 10;
  } else if (rangeWithPadding <= 100) {
    range = Math.ceil(rangeWithPadding / 20) * 20;
  } else {
    range = Math.ceil(rangeWithPadding / 50) * 50;
  }
  
  return [-range, range];
};

// Helper for non-symmetric Y domain (for sync which is always positive)
const getYDomainForSync = (regressionData) => {
  if (!regressionData || regressionData.length === 0) return [0, 50];
  
  let maxVal = 0;
  regressionData.forEach(series => {
    series.data.forEach(point => {
      const upper = point.value + (series.stdDev || 0);
      maxVal = Math.max(maxVal, upper);
    });
  });
  
  const rangeWithPadding = maxVal * 1.2;
  
  let range;
  if (rangeWithPadding <= 10) {
    range = Math.ceil(rangeWithPadding);
  } else if (rangeWithPadding <= 25) {
    range = Math.ceil(rangeWithPadding / 5) * 5;
  } else if (rangeWithPadding <= 50) {
    range = Math.ceil(rangeWithPadding / 10) * 10;
  } else if (rangeWithPadding <= 100) {
    range = Math.ceil(rangeWithPadding / 20) * 20;
  } else {
    range = Math.ceil(rangeWithPadding / 50) * 50;
  }
  
  return [0, range];
};

// Helper to generate Y-axis ticks
const generateYTicks = (domain) => {
  const [min, max] = domain;
  const range = max - min;
  
  let step;
  if (range <= 10) {
    step = 2;
  } else if (range <= 25) {
    step = 5;
  } else if (range <= 50) {
    step = 10;
  } else if (range <= 100) {
    step = 20;
  } else {
    step = 50;
  }
  
  const ticks = [];
  
  if (min === 0) {
    // For non-symmetric (sync), start from 0
    ticks.push(0);
    for (let i = step; i <= max; i += step) {
      ticks.push(i);
    }
  } else {
    // For symmetric (lateness), center on 0
    ticks.push(0);
    for (let i = step; i <= max; i += step) {
      ticks.push(i);
    }
    for (let i = -step; i >= min; i -= step) {
      ticks.unshift(i);
    }
  }
  
  return ticks;
};

const LatenessOverview = ({ sessions, filteredSessions, timeMode }) => {
  // Calculate synchronization
  const syncValue = timeMode === 'trend'
    ? calculateTrend(filteredSessions, 'synchronization')
    : { simple: calculateSynchronization(filteredSessions) };

  // Determine which hand is late/early for synchronization display
  const syncHand = syncValue.simple ? (syncValue.simple > 0 ? 'RH' : 'LH') : null;
  const syncDirection = syncValue.simple ? (syncValue.simple > 0 ? 'late by' : 'early by') : null;
  const syncMagnitude = syncValue.simple ? Math.abs(syncValue.simple) : null;

  // Get earliest/latest fingers (worst offenders for late/early)
  const leftFingers = getEarliestLatestFingers(filteredSessions, 'left');
  const rightFingers = getEarliestLatestFingers(filteredSessions, 'right');

  // Get worst offenders for sync
  const leftFingersSync = getWorstOffendersSync(filteredSessions, 'left');
  const rightFingersSync = getWorstOffendersSync(filteredSessions, 'right');

  // Get worst offenders for scales and arpeggios
  const scaleSessions = useMemo(() => sessions.filter(s => s.exercise_type === 'scale'), [sessions]);
  const arpeggioSessions = useMemo(() => sessions.filter(s => s.exercise_type === 'arpeggio'), [sessions]);
  
  const leftFingersScales = useMemo(() => getEarliestLatestFingers(scaleSessions, 'left'), [scaleSessions]);
  const rightFingersScales = useMemo(() => getEarliestLatestFingers(scaleSessions, 'right'), [scaleSessions]);
  
  const leftFingersArpeggios = useMemo(() => getEarliestLatestFingers(arpeggioSessions, 'left'), [arpeggioSessions]);
  const rightFingersArpeggios = useMemo(() => getEarliestLatestFingers(arpeggioSessions, 'right'), [arpeggioSessions]);

  // Prepare data for lateness trend by repetition (always use all sessions)
  const leftTrendRepetition = prepareTrendOverRepetitionData(
    sessions,
    'left',
    [leftFingers.earliest?.finger, leftFingers.latest?.finger].filter(Boolean)
  );
  const rightTrendRepetition = prepareTrendOverRepetitionData(
    sessions,
    'right',
    [rightFingers.earliest?.finger, rightFingers.latest?.finger].filter(Boolean)
  );

  // Prepare data for sync trend by repetition
  const leftSyncRepetition = prepareSyncTrendOverRepetitionData(
    sessions,
    'left',
    [leftFingersSync.worst?.finger].filter(Boolean)
  );
  const rightSyncRepetition = prepareSyncTrendOverRepetitionData(
    sessions,
    'right',
    [rightFingersSync.worst?.finger].filter(Boolean)
  );

  // Colors - match volume analysis
  const leftColor = '#9333ea';
  const rightColor = '#ea580c';
  const purpleShades = ['#6b21a8', '#9333ea', '#a855f7', '#c084fc'];
  const orangeShades = ['#ea580c', '#f97316', '#fb923c', '#fdba74'];

  // Create regression data for lateness trend
  const latenessRegressionData = [];
  
  Object.entries(leftTrendRepetition).forEach(([finger, data], idx) => {
    if (data.regression && data.points.length > 0) {
      const values = data.points.map(p => p.y);
      const stdDev = calculateStdDev(values);
      const minX = Math.min(...data.points.map(p => p.x));
      const maxX = Math.max(...data.points.map(p => p.x));
      
      const points = [];
      const step = Math.max(1, (maxX - minX) / 20);
      for (let x = minX; x <= maxX; x += step) {
        const y = data.regression.slope * x + data.regression.intercept;
        points.push({
          repetition: x,
          value: y,
          upper: y + stdDev,
          lower: y - stdDev
        });
      }
      
      latenessRegressionData.push({
        finger: `L${finger}`,
        color: purpleShades[idx % purpleShades.length],
        data: points,
        stdDev
      });
    }
  });
  
  Object.entries(rightTrendRepetition).forEach(([finger, data], idx) => {
    if (data.regression && data.points.length > 0) {
      const values = data.points.map(p => p.y);
      const stdDev = calculateStdDev(values);
      const minX = Math.min(...data.points.map(p => p.x));
      const maxX = Math.max(...data.points.map(p => p.x));
      
      const points = [];
      const step = Math.max(1, (maxX - minX) / 20);
      for (let x = minX; x <= maxX; x += step) {
        const y = data.regression.slope * x + data.regression.intercept;
        points.push({
          repetition: x,
          value: y,
          upper: y + stdDev,
          lower: y - stdDev
        });
      }
      
      latenessRegressionData.push({
        finger: `R${finger}`,
        color: orangeShades[idx % orangeShades.length],
        data: points,
        stdDev
      });
    }
  });

  // Create regression data for sync trend
  const syncRegressionData = [];
  
  Object.entries(leftSyncRepetition).forEach(([finger, data], idx) => {
    if (data.regression && data.points.length > 0) {
      const values = data.points.map(p => p.y);
      const stdDev = calculateStdDev(values);
      const minX = Math.min(...data.points.map(p => p.x));
      const maxX = Math.max(...data.points.map(p => p.x));
      
      const points = [];
      const step = Math.max(1, (maxX - minX) / 20);
      for (let x = minX; x <= maxX; x += step) {
        const y = data.regression.slope * x + data.regression.intercept;
        points.push({
          repetition: x,
          value: y,
          upper: y + stdDev,
          lower: y - stdDev
        });
      }
      
      syncRegressionData.push({
        finger: `L${finger}`,
        color: purpleShades[idx % purpleShades.length],
        data: points,
        stdDev
      });
    }
  });
  
  Object.entries(rightSyncRepetition).forEach(([finger, data], idx) => {
    if (data.regression && data.points.length > 0) {
      const values = data.points.map(p => p.y);
      const stdDev = calculateStdDev(values);
      const minX = Math.min(...data.points.map(p => p.x));
      const maxX = Math.max(...data.points.map(p => p.x));
      
      const points = [];
      const step = Math.max(1, (maxX - minX) / 20);
      for (let x = minX; x <= maxX; x += step) {
        const y = data.regression.slope * x + data.regression.intercept;
        points.push({
          repetition: x,
          value: y,
          upper: y + stdDev,
          lower: y - stdDev
        });
      }
      
      syncRegressionData.push({
        finger: `R${finger}`,
        color: orangeShades[idx % orangeShades.length],
        data: points,
        stdDev
      });
    }
  });

  // Calculate Y domains
  const latenessYDomain = getSymmetricYDomainForRegression(latenessRegressionData);
  const syncYDomain = getYDomainForSync(syncRegressionData);

  // Custom legend component
  const renderCustomLegend = (data) => {
    return (
      <div className="flex flex-col gap-1 text-[9px]">
        {data.map((item, idx) => (
          <div key={idx} className="flex items-center gap-1">
            <div className="w-2 h-0.5" style={{ backgroundColor: item.color }}></div>
            <span>{item.finger}</span>
          </div>
        ))}
      </div>
    );
  };

  // Render worst offenders table for a specific type
  const renderWorstOffenders = (type, leftFingers, rightFingers) => {
    return (
      <div className="bg-gray-50 p-2 rounded">
        <div className="text-xs font-semibold text-gray-600 mb-1">
          Worst Offenders ({type === 'scale' ? 'Scales' : 'Arpeggios'})
        </div>
        <table className="w-full text-[10px]">
          <thead>
            <tr className="border-b border-gray-300">
              <th className="text-left py-0.5"></th>
              <th className="text-center py-0.5 font-semibold" style={{ color: '#9333ea' }}>Left</th>
              <th className="text-center py-0.5 font-semibold" style={{ color: '#ea580c' }}>Right</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="py-0.5 font-semibold text-gray-600">Early</td>
              <td className="text-center py-0.5">
                {leftFingers.earliest && leftFingers.earliest.avg < 0 ? (
                  <>
                    <span className="font-bold" style={{ color: '#9333ea' }}>
                      {leftFingers.earliest.finger}
                    </span>
                    <span className="text-gray-500 ml-1 text-[9px]">
                      ({Math.abs(leftFingers.earliest.avg).toFixed(1)}ms)
                    </span>
                  </>
                ) : (
                  <span>-</span>
                )}
              </td>
              <td className="text-center py-0.5">
                {rightFingers.earliest && rightFingers.earliest.avg < 0 ? (
                  <>
                    <span className="font-bold" style={{ color: '#ea580c' }}>
                      {rightFingers.earliest.finger}
                    </span>
                    <span className="text-gray-500 ml-1 text-[9px]">
                      ({Math.abs(rightFingers.earliest.avg).toFixed(1)}ms)
                    </span>
                  </>
                ) : (
                  <span>-</span>
                )}
              </td>
            </tr>
            <tr>
              <td className="py-0.5 font-semibold text-gray-600">Late</td>
              <td className="text-center py-0.5">
                {leftFingers.latest && leftFingers.latest.avg > 0 ? (
                  <>
                    <span className="font-bold" style={{ color: '#9333ea' }}>
                      {leftFingers.latest.finger}
                    </span>
                    <span className="text-gray-500 ml-1 text-[9px]">
                      ({leftFingers.latest.avg.toFixed(1)}ms)
                    </span>
                  </>
                ) : (
                  <span>-</span>
                )}
              </td>
              <td className="text-center py-0.5">
                {rightFingers.latest && rightFingers.latest.avg > 0 ? (
                  <>
                    <span className="font-bold" style={{ color: '#ea580c' }}>
                      {rightFingers.latest.finger}
                    </span>
                    <span className="text-gray-500 ml-1 text-[9px]">
                      ({rightFingers.latest.avg.toFixed(1)}ms)
                    </span>
                  </>
                ) : (
                  <span>-</span>
                )}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="flex gap-3">
      {/* Left Side: Sync and Worst Offenders */}
      <div className="flex flex-col gap-3" style={{ width: '350px' }}>
        {/* Top Row: Synchronization and Worst Offenders (Out of Sync) side by side */}
        <div className="flex gap-3">
          {/* Synchronization */}
          <div className="bg-gray-50 p-2 rounded flex-1">
            <div className="text-xs font-semibold text-gray-600 mb-1">Synchronization</div>
            {timeMode === 'trend' ? (
              <div className="text-[10px] space-y-0.5">
                <div className="flex justify-between">
                  <span className="text-gray-600">5d:</span>
                  <span className="font-medium">{syncValue.last5?.toFixed(1) || 'N/A'}ms</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">30d:</span>
                  <span className="font-medium">{syncValue.last30?.toFixed(1) || 'N/A'}ms</span>
                </div>
                <div className="flex justify-between pt-0.5 border-t border-gray-300">
                  <span className="text-gray-600 font-semibold">Δ:</span>
                  <span className={`font-bold ${syncValue.difference > 0 ? 'text-orange-600' : 'text-purple-600'}`}>
                    {syncValue.difference?.toFixed(1) || 'N/A'}ms
                  </span>
                </div>
              </div>
            ) : (
              <p className="text-xs font-medium">
                {syncHand && syncDirection && syncMagnitude != null 
                  ? `${syncHand} ${syncDirection} ${syncMagnitude.toFixed(1)}ms` 
                  : 'N/A'}
              </p>
            )}
          </div>

          {/* Worst Offenders (Out of Sync) */}
          <div className="bg-gray-50 p-2 rounded flex-1">
            <div className="text-xs font-semibold text-gray-600 mb-1">Worst Offenders (Out of Sync)</div>
            <div className="flex items-center justify-between text-[10px] py-1">
              <div className="flex items-center gap-1">
                <span className="font-semibold" style={{ color: '#9333ea' }}>Left:</span>
                <span className="font-bold text-gray-900">
                  {leftFingersSync.worst ? leftFingersSync.worst.finger : 'N/A'}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <span className="font-semibold" style={{ color: '#ea580c' }}>Right:</span>
                <span className="font-bold text-gray-900">
                  {rightFingersSync.worst ? rightFingersSync.worst.finger : 'N/A'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Row: Worst Offenders (Late/Early) spanning full width */}
        <div className="bg-gray-50 p-2 rounded">
          <div className="text-xs font-semibold text-gray-600 mb-1">Worst Offenders (Late/Early)</div>
          <table className="w-full text-[10px]">
            <thead>
              <tr className="border-b border-gray-300">
                <th className="text-left py-0.5"></th>
                <th className="text-center py-0.5 font-semibold" style={{ color: '#9333ea' }}>Left</th>
                <th className="text-center py-0.5 font-semibold" style={{ color: '#ea580c' }}>Right</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="py-0.5 font-semibold text-gray-600">Early</td>
                <td className="text-center py-0.5">
                  {leftFingers.earliest && leftFingers.earliest.avg < 0 ? (
                    <>
                      <span className="font-bold" style={{ color: '#9333ea' }}>
                        {leftFingers.earliest.finger}
                      </span>
                      <span className="text-gray-500 ml-1 text-[9px]">
                        ({Math.abs(leftFingers.earliest.avg).toFixed(1)}ms)
                      </span>
                    </>
                  ) : (
                    <span>-</span>
                  )}
                </td>
                <td className="text-center py-0.5">
                  {rightFingers.earliest && rightFingers.earliest.avg < 0 ? (
                    <>
                      <span className="font-bold" style={{ color: '#ea580c' }}>
                        {rightFingers.earliest.finger}
                      </span>
                      <span className="text-gray-500 ml-1 text-[9px]">
                        ({Math.abs(rightFingers.earliest.avg).toFixed(1)}ms)
                      </span>
                    </>
                  ) : (
                    <span>-</span>
                  )}
                </td>
              </tr>
              <tr>
                <td className="py-0.5 font-semibold text-gray-600">Late</td>
                <td className="text-center py-0.5">
                  {leftFingers.latest && leftFingers.latest.avg > 0 ? (
                    <>
                      <span className="font-bold" style={{ color: '#9333ea' }}>
                        {leftFingers.latest.finger}
                      </span>
                      <span className="text-gray-500 ml-1 text-[9px]">
                        ({leftFingers.latest.avg.toFixed(1)}ms)
                      </span>
                    </>
                  ) : (
                    <span>-</span>
                  )}
                </td>
                <td className="text-center py-0.5">
                  {rightFingers.latest && rightFingers.latest.avg > 0 ? (
                    <>
                      <span className="font-bold" style={{ color: '#ea580c' }}>
                        {rightFingers.latest.finger}
                      </span>
                      <span className="text-gray-500 ml-1 text-[9px]">
                        ({rightFingers.latest.avg.toFixed(1)}ms)
                      </span>
                    </>
                  ) : (
                    <span>-</span>
                  )}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Worst Offenders for Scales */}
        {/* {scaleSessions.length > 0 && renderWorstOffenders('scale', leftFingersScales, rightFingersScales)} */}

        {/* Worst Offenders for Arpeggios */}
        {/* {arpeggioSessions.length > 0 && renderWorstOffenders('arpeggio', leftFingersArpeggios, rightFingersArpeggios)} */}
      </div>

      {/* Right Side: Graphs - Horizontal Layout (2 columns) */}
      <div className="flex-1 grid grid-cols-2 gap-2">
        {/* Lateness Trend by Repetition */}
        <div className="bg-gray-50 p-2 rounded">
          <h3 className="text-[10px] font-semibold text-gray-700 mb-1">Lateness Trend by Repetition</h3>
          {(Object.keys(leftTrendRepetition).length > 0 || Object.keys(rightTrendRepetition).length > 0) ? (
            <div className="flex gap-1">
              <ResponsiveContainer width="80%" height={120}>
                <ComposedChart margin={{ top: 2, right: 2, bottom: 2, left: 2 }}>
                  <defs>
                    {latenessRegressionData.map((regData, idx) => (
                      <linearGradient key={`gradient-${idx}`} id={`lateness-gradient-rep-${idx}`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={regData.color} stopOpacity={0.3} />
                        <stop offset="100%" stopColor={regData.color} stopOpacity={0.1} />
                      </linearGradient>
                    ))}
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#ddd" />
                  <ReferenceArea y1={-5} y2={5} fill="#10b981" fillOpacity={0.1} />
                  <XAxis 
                    type="number"
                    dataKey="repetition" 
                    domain={['dataMin', 'dataMax']}
                    hide
                  />
                  <YAxis 
                    type="number"
                    domain={latenessYDomain}
                    ticks={generateYTicks(latenessYDomain)}
                    tick={{ fontSize: 8 }}
                    width={25}
                  />
                  <Tooltip
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        const data = payload[0].payload;
                        return (
                          <div className="bg-white p-1 border border-gray-300 rounded shadow-sm">
                            <p className="text-[9px]">Rep: #{Math.round(data.repetition)}</p>
                            <p className="text-[9px]">{data.value?.toFixed(1)}ms</p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  {/* Raw scatter points for left fingers */}
                  {Object.entries(leftTrendRepetition).map(([finger, data], idx) => {
                    if (!data.points || data.points.length === 0) return null;
                    const scatterData = data.points.map(p => ({ repetition: p.x, value: p.y }));
                    return (
                      <Scatter
                        key={`left-rep-scatter-${finger}`}
                        data={scatterData}
                        fill={leftColor}
                        fillOpacity={0.6}
                        r={3}
                        isAnimationActive={false}
                      />
                    );
                  })}
                  {/* Raw scatter points for right fingers */}
                  {Object.entries(rightTrendRepetition).map(([finger, data], idx) => {
                    if (!data.points || data.points.length === 0) return null;
                    const scatterData = data.points.map(p => ({ repetition: p.x, value: p.y }));
                    return (
                      <Scatter
                        key={`right-rep-scatter-${finger}`}
                        data={scatterData}
                        fill={rightColor}
                        fillOpacity={0.6}
                        r={3}
                        isAnimationActive={false}
                      />
                    );
                  })}
                  {/* Regression lines with std dev bands */}
                  {latenessRegressionData.map((regData, idx) => (
                    <React.Fragment key={`series-${idx}`}>
                      <Area
                        data={regData.data}
                        type="monotone"
                        dataKey="upper"
                        stroke="none"
                        fill={`url(#lateness-gradient-rep-${idx})`}
                        fillOpacity={0.3}
                        isAnimationActive={false}
                      />
                      <Area
                        data={regData.data}
                        type="monotone"
                        dataKey="lower"
                        stroke="none"
                        fill="transparent"
                        isAnimationActive={false}
                      />
                      <Line
                        data={regData.data}
                        type="monotone"
                        dataKey="value"
                        stroke={regData.color}
                        strokeWidth={1.5}
                        dot={false}
                        isAnimationActive={false}
                      />
                    </React.Fragment>
                  ))}
                </ComposedChart>
              </ResponsiveContainer>
              {renderCustomLegend(latenessRegressionData)}
            </div>
          ) : (
            <p className="text-[9px] text-gray-500 text-center py-4">No data</p>
          )}
        </div>

        {/* Sync Trend by Repetition */}
        <div className="bg-gray-50 p-2 rounded">
          <h3 className="text-[10px] font-semibold text-gray-700 mb-1">Sync Trend by Repetition</h3>
          {(Object.keys(leftSyncRepetition).length > 0 || Object.keys(rightSyncRepetition).length > 0) ? (
            <div className="flex gap-1">
              <ResponsiveContainer width="80%" height={120}>
                <ComposedChart margin={{ top: 2, right: 2, bottom: 2, left: 2 }}>
                  <defs>
                    {syncRegressionData.map((regData, idx) => (
                      <linearGradient key={`gradient-sync-${idx}`} id={`sync-gradient-rep-${idx}`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={regData.color} stopOpacity={0.3} />
                        <stop offset="100%" stopColor={regData.color} stopOpacity={0.1} />
                      </linearGradient>
                    ))}
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#ddd" />
                  <XAxis 
                    type="number"
                    dataKey="repetition" 
                    domain={['dataMin', 'dataMax']}
                    hide
                  />
                  <YAxis 
                    type="number"
                    domain={syncYDomain}
                    ticks={generateYTicks(syncYDomain)}
                    tick={{ fontSize: 8 }}
                    width={25}
                  />
                  <Tooltip
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        const data = payload[0].payload;
                        return (
                          <div className="bg-white p-1 border border-gray-300 rounded shadow-sm">
                            <p className="text-[9px]">Rep: #{Math.round(data.repetition)}</p>
                            <p className="text-[9px]">{data.value?.toFixed(1)}ms</p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  {/* Raw scatter points for left fingers */}
                  {Object.entries(leftSyncRepetition).map(([finger, data], idx) => {
                    if (!data.points || data.points.length === 0) return null;
                    const scatterData = data.points.map(p => ({ repetition: p.x, value: p.y }));
                    return (
                      <Scatter
                        key={`left-sync-scatter-${finger}`}
                        data={scatterData}
                        fill={leftColor}
                        fillOpacity={0.6}
                        r={3}
                        isAnimationActive={false}
                      />
                    );
                  })}
                  {/* Raw scatter points for right fingers */}
                  {Object.entries(rightSyncRepetition).map(([finger, data], idx) => {
                    if (!data.points || data.points.length === 0) return null;
                    const scatterData = data.points.map(p => ({ repetition: p.x, value: p.y }));
                    return (
                      <Scatter
                        key={`right-sync-scatter-${finger}`}
                        data={scatterData}
                        fill={rightColor}
                        fillOpacity={0.6}
                        r={3}
                        isAnimationActive={false}
                      />
                    );
                  })}
                  {/* Regression lines with std dev bands */}
                  {syncRegressionData.map((regData, idx) => (
                    <React.Fragment key={`series-sync-${idx}`}>
                      <Area
                        data={regData.data}
                        type="monotone"
                        dataKey="upper"
                        stroke="none"
                        fill={`url(#sync-gradient-rep-${idx})`}
                        fillOpacity={0.3}
                        isAnimationActive={false}
                      />
                      <Area
                        data={regData.data}
                        type="monotone"
                        dataKey="lower"
                        stroke="none"
                        fill="transparent"
                        isAnimationActive={false}
                      />
                      <Line
                        data={regData.data}
                        type="monotone"
                        dataKey="value"
                        stroke={regData.color}
                        strokeWidth={1.5}
                        dot={false}
                        isAnimationActive={false}
                      />
                    </React.Fragment>
                  ))}
                </ComposedChart>
              </ResponsiveContainer>
              {renderCustomLegend(syncRegressionData)}
            </div>
          ) : (
            <p className="text-[9px] text-gray-500 text-center py-4">No data</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default LatenessOverview;
